/**
 * Cliente HTTP:
 *  - access token no header; refresh por cookie httpOnly em 401
 *  - refresh único compartilhado entre requisições simultâneas
 *  - envelope de erro consistente (RFC v1.1 §14)
 *  - upload multipart e download autenticado
 */
import { useAuth } from "@/hooks/useAuth";

const API_BASE = "/api/v1";

export interface ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  correlationId?: string;
}

interface Refreshed { accessToken: string; expiresAt: string; remember?: boolean }

let refreshing: Promise<boolean> | null = null;

// Atividade da pessoa na tela (para o limite de inatividade da sessão comum: 4h).
let lastActivity = Date.now();
export const IDLE_LIMIT_MS = 4 * 3_600_000;
export function idleMs(): number { return Date.now() - lastActivity; }
if (typeof window !== "undefined") {
  const bump = () => { lastActivity = Date.now(); };
  for (const ev of ["mousedown", "keydown", "wheel", "touchstart", "scroll"]) window.addEventListener(ev, bump, { passive: true, capture: true });
}

/**
 * Troca o cookie de refresh por um access token novo.
 * Várias abas abertas dividem o mesmo cookie: a renovação passa por uma trava entre abas
 * (Web Locks), senão duas abas renovando juntas pareciam "token reutilizado" e a sessão caía.
 */
export async function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    const once = async (): Promise<Response> => fetch(`${API_BASE}/auth/refresh`, {
      method: "POST", credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ idleMs: Math.floor(idleMs()) }),
    });
    const run = async (): Promise<boolean> => {
      try {
        let res = await once();
        // Outra aba acabou de renovar: o cookie já é o novo — tenta de novo uma vez.
        if (res.status === 401) { await new Promise((r) => setTimeout(r, 400)); res = await once(); }
        if (!res.ok) return false;
        const r = (await res.json()) as Refreshed;
        useAuth.getState().setSession({ accessToken: r.accessToken, expiresAt: r.expiresAt, remember: r.remember });
        return true;
      } catch {
        return false;
      }
    };
    // Trava entre abas (Web Locks). Tipagem própria: as versões da lib DOM diferem entre ambientes.
    const locks = typeof navigator !== "undefined" ? (navigator as unknown as { locks?: { request: (n: string, f: () => unknown) => Promise<unknown> } }).locks : undefined;
    const p: Promise<boolean> = locks ? locks.request("vg-refresh", run).then((v) => v === true) : run();
    const done: Promise<boolean> = p.finally(() => { setTimeout(() => { refreshing = null; }, 0); });
    refreshing = done;
    return done;
  }
  return refreshing;
}

async function raw(method: string, path: string, body?: BodyInit, contentType?: string, retry = true): Promise<Response> {
  const { session, clear } = useAuth.getState();
  const headers: Record<string, string> = { Accept: "application/json" };
  // Só declara JSON quando há corpo: o Fastify rejeita (400) JSON vazio.
  if (contentType) headers["Content-Type"] = contentType;
  if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { method, headers, body, credentials: "include" });
  } catch {
    // Sem resposta (servidor reiniciando numa atualização, internet caiu). Leitura: tenta de novo uma vez.
    // Gravação não repete sozinha (poderia gravar duas vezes).
    try {
      if (method !== "GET") throw new Error("sem repetição");
      await new Promise((r) => setTimeout(r, 2000));
      res = await fetch(`${API_BASE}${path}`, { method, headers, body, credentials: "include" });
    } catch {
      const err = new Error("Sem conexão com o servidor. O sistema pode estar sendo atualizado ou a internet caiu — aguarde alguns segundos e tente de novo.") as ApiError;
      err.code = "sem_conexao"; err.status = 0;
      throw err;
    }
  }
  if (res.status === 401 && retry && !path.startsWith("/auth/")) {
    if (await refreshSession()) return raw(method, path, body, contentType, false);
    clear();
  }
  return res;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await raw(method, path, body === undefined ? undefined : JSON.stringify(body), body === undefined ? undefined : "application/json");
  if (!res.ok) {
    const err = await toApiError(res);
    // Política do escritório exige 2FA para o papel: leva para Minha conta.
    if (err.code === "mfa_required" && !window.location.pathname.startsWith("/conta")) {
      window.history.pushState(null, "", "/conta?mfa=obrigatorio");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function toApiError(res: Response): Promise<ApiError> {
  let payload: { code?: string; message?: string | string[]; details?: unknown; correlation_id?: string } = {};
  try { payload = await res.json(); } catch { /* ignore */ }
  const msg = Array.isArray(payload.message) ? payload.message.join("; ") : payload.message;
  const err = new Error(msg ?? `HTTP ${res.status}`) as ApiError;
  err.code = payload.code ?? `http_${res.status}`;
  err.status = res.status;
  err.details = payload.details;
  err.correlationId = payload.correlation_id;
  return err;
}

/** Monta querystring ignorando vazios. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "" && v !== false) u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const api = {
  get:  <T,>(path: string) => request<T>("GET", path),
  post: <T,>(path: string, body?: unknown) => request<T>("POST", path, body),
  put:  <T,>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del:  <T,>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  patch:<T,>(path: string, body?: unknown) => request<T>("PATCH", path, body),

  /** POST cuja resposta chega em partes (text/event-stream). Erro antes do streaming vira ApiError normal. */
  async stream(path: string, body: unknown): Promise<Response> {
    const res = await raw("POST", path, JSON.stringify(body), "application/json");
    if (!res.ok) throw await toApiError(res);
    return res;
  },

  async upload<T>(path: string, form: FormData): Promise<T> {
    const res = await raw("POST", path, form);
    if (!res.ok) throw await toApiError(res);
    return (await res.json()) as T;
  },

  /** POST que devolve um arquivo (ex.: PDF de teste) e abre numa aba nova. */
  async openPost(path: string, body: unknown): Promise<void> {
    const res = await raw("POST", path, JSON.stringify(body), "application/json");
    if (!res.ok) throw await toApiError(res);
    const url = URL.createObjectURL(await res.blob());
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** POST que devolve um arquivo para baixar (ex.: planilha exportada). */
  async downloadPost(path: string, body: unknown, filename: string): Promise<void> {
    const res = await raw("POST", path, JSON.stringify(body), "application/json");
    if (!res.ok) throw await toApiError(res);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Envia um arquivo e baixa o arquivo devolvido (ex.: planilha com listas atualizadas). */
  async uploadDownload(path: string, form: FormData, filename: string): Promise<void> {
    const res = await raw("POST", path, form);
    if (!res.ok) throw await toApiError(res);
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** URL local (blob:) de um arquivo autenticado, para <img>. */
  async blobUrl(path: string): Promise<string> {
    const res = await raw("GET", path);
    if (!res.ok) throw await toApiError(res);
    return URL.createObjectURL(await res.blob());
  },

  /** Baixa (ou abre) um arquivo autenticado. */
  async download(path: string, filename: string, inline = false): Promise<void> {
    const res = await raw("GET", path);
    if (!res.ok) throw await toApiError(res);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (inline) {
      window.open(url, "_blank", "noopener");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};
